// Form5 Utilities

import { toast } from "@/hooks/useToast";
import type { ListItem, TreeConfig } from "./types";
import { DEFAULT_MESSAGES, DEFAULT_UI, DEFAULT_FIELDS } from "./constants";

// ---------------------------------------------------------------------------
// Toast Helpers
// ---------------------------------------------------------------------------

export const showToast = {
  success: (message: string) => toast({ title: "Success", description: message }),
  error: (message: string) => toast({ title: "Error", description: message, variant: "destructive" }),
  validation: (title: string, description: string) =>
    toast({ title, description, variant: "destructive" }),
};

// ---------------------------------------------------------------------------
// Response Extractors
// ---------------------------------------------------------------------------

export const extractItemsFromResponse = (response: any): ListItem[] => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (response.data && Array.isArray(response.data)) return response.data;
  if (response.data?.data && Array.isArray(response.data.data)) return response.data.data;
  return [];
};

export const extractIdFromResponse = (response: any): number | string | undefined => {
  return response?.data?.id || response?.id;
};

// ---------------------------------------------------------------------------
// Config Getters
// ---------------------------------------------------------------------------

export const getTreeConfig = (treeConfig?: TreeConfig) => ({
  treeIcon: treeConfig?.treeIcon || DEFAULT_UI.TREE_ICON,
  treeTitle: treeConfig?.treeTitle || DEFAULT_UI.TREE_TITLE,
  emptyMessage: treeConfig?.emptyMessage || DEFAULT_UI.EMPTY_MESSAGE,
  primaryField: treeConfig?.itemDisplayFields?.primary || DEFAULT_FIELDS.PRIMARY,
  secondaryField: treeConfig?.itemDisplayFields?.secondary || DEFAULT_FIELDS.SECONDARY,
  formLabels: {
    newButton: treeConfig?.formLabels?.newButton || DEFAULT_UI.NEW_BUTTON,
    edit: treeConfig?.formLabels?.edit || DEFAULT_UI.EDIT_LABEL,
    addNew: treeConfig?.formLabels?.addNew || DEFAULT_UI.ADD_NEW_LABEL,
    updateButton: treeConfig?.formLabels?.updateButton || DEFAULT_UI.UPDATE_BUTTON,
    saveButton: treeConfig?.formLabels?.saveButton || DEFAULT_UI.SAVE_BUTTON,
    cancelButton: treeConfig?.formLabels?.cancelButton || DEFAULT_UI.CANCEL_BUTTON,
  },
  deleteConfirmation: treeConfig?.deleteConfirmation,
});

// ---------------------------------------------------------------------------
// Payload Builder
// ---------------------------------------------------------------------------

export const buildPayload = (
  fields: Record<string, any>,
  universitySettingsId: number,
  isUpdate: boolean,
  userId?: number
): Record<string, any> => {
  const { id, ...fieldsWithoutId } = fields;
  
  return {
    ...fieldsWithoutId,
    university_settings_id: universitySettingsId,
    ...(isUpdate ? { updated_by: userId } : { created_by: userId }),
  };
};

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export const getUniversitySettingsId = (
  componentId: string | undefined,
  listItems: ListItem[]
): number | null => {
  if (componentId && !isNaN(Number(componentId)) && Number(componentId) > 0) {
    return Number(componentId);
  }
  if (listItems.length > 0 && listItems[0].university_settings_id) {
    return Number(listItems[0].university_settings_id);
  }
  return null;
};

// ---------------------------------------------------------------------------
// Field Defaults
// ---------------------------------------------------------------------------

export const getDefaultFieldValues = (fieldsConfig: any[]): Record<string, any> => {
  const defaults: Record<string, any> = {};
  fieldsConfig.forEach((field: any) => {
    if (field.defaultValue !== undefined) {
      defaults[field.fieldName] = field.defaultValue;
    }
  });
  return defaults;
};

export const mapItemToFormFields = (
  item: ListItem,
  fieldsConfig: any[]
): Record<string, any> => {
  const formData: Record<string, any> = {};
  fieldsConfig.forEach((field: any) => {
    const fieldName = field.fieldName;
    formData[fieldName] = item[fieldName] !== undefined ? item[fieldName] : field.defaultValue;
  });
  return formData;
};

